import { Link } from 'wouter'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { SEOHead } from '@/components/seo-head'
import { Check, ArrowRight, HelpCircle } from 'lucide-react'

export function Pricing() {
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "PriceSpecification",
    "name": "SourcedIn RFP Marketplace Pricing",
    "description": "Transparent pricing for RFP marketplace access and premium features",
    "offers": [
      {
        "@type": "Offer",
        "name": "Free Plan",
        "price": "0",
        "priceCurrency": "USD",
        "description": "Basic access to RFP marketplace with limited features"
      },
      {
        "@type": "Offer", 
        "name": "Pro Plan",
        "price": "199",
        "priceCurrency": "USD",
        "billingIncrement": "P1M",
        "description": "Full access to all RFP marketplace features and premium tools"
      }
    ]
  }

  const faqStructuredData = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "What's included in the Free plan?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "The Free plan includes access to browse RFPs, create a basic vendor profile, and submit up to 3 proposals per month."
        }
      },
      {
        "@type": "Question",
        "name": "Can I upgrade or downgrade my plan anytime?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Yes, you can upgrade or downgrade your plan at any time. Changes take effect immediately and billing is prorated."
        }
      }
    ]
  }

  return (
    <div className="min-h-screen bg-white">
      <SEOHead
        title="SourcedIn Pricing - RFP Marketplace Plans | Free & Pro Options"
        description="Transparent pricing for SourcedIn's RFP marketplace. Choose from free basic access or Pro plan with unlimited RFPs, advanced matching, and premium features. No hidden fees."
        keywords="RFP marketplace pricing, request for proposal platform cost, B2B marketplace pricing, vendor subscription plans, procurement platform pricing"
        canonical="https://sourcedin.ai/pricing"
        structuredData={[structuredData, faqStructuredData]}
      />

      {/* Hero Section */}
      <section className="py-20 px-4 bg-gradient-to-br from-blue-50 to-purple-50">
        <div className="max-w-6xl mx-auto text-center">
          <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">
            Simple, Transparent 
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600"> Pricing</span>
          </h1>
          <p className="text-xl md:text-2xl text-gray-600 max-w-4xl mx-auto leading-relaxed mb-8">
            Choose the perfect plan for your business. Start free, upgrade when you're ready. 
            No hidden fees, no long-term contracts.
          </p>
        </div>
      </section>

      {/* Pricing Cards */}
      <section className="py-20 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {/* Free Plan */}
            <Card className="border-2 border-gray-200 hover:border-gray-300 transition-colors">
              <CardHeader className="text-center pb-6">
                <CardTitle className="text-2xl font-bold text-gray-900">Free</CardTitle>
                <CardDescription className="text-gray-600 mb-4">
                  Perfect for getting started with RFP marketplace
                </CardDescription>
                <div className="text-center">
                  <span className="text-5xl font-bold text-gray-900">$0</span>
                  <span className="text-gray-600 ml-2">/month</span>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-3">
                  <div className="flex items-center space-x-3">
                    <Check className="h-5 w-5 text-green-600" />
                    <span className="text-gray-700">Browse unlimited RFPs</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <Check className="h-5 w-5 text-green-600" />
                    <span className="text-gray-700">Basic vendor profile</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <Check className="h-5 w-5 text-green-600" />
                    <span className="text-gray-700">Basic messaging</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <Check className="h-5 w-5 text-green-600" />
                    <span className="text-gray-700">Email support</span>
                  </div>
                </div>
                <div className="pt-4">
                  <Link href="/register">
                    <Button variant="outline" className="w-full text-lg py-3">
                      Get Started Free
                    </Button>
                  </Link>
                </div>
              </CardContent>
            </Card>

            {/* Pro Plan */}
            <Card className="border-2 border-blue-500 relative hover:border-blue-600 transition-colors shadow-lg">
              <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                <span className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-4 py-1 rounded-full text-sm font-medium">
                  Most Popular
                </span>
              </div>
              <CardHeader className="text-center pb-6">
                <CardTitle className="text-2xl font-bold text-gray-900">Pro</CardTitle>
                <CardDescription className="text-gray-600 mb-4">
                  Everything you need to win more business
                </CardDescription>
                <div className="text-center">
                  <span className="text-5xl font-bold text-gray-900">$199</span>
                  <span className="text-gray-600 ml-2">/month</span>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-3">
                  <div className="flex items-center space-x-3">
                    <Check className="h-5 w-5 text-green-600" />
                    <span className="text-gray-700">Everything in Free</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <Check className="h-5 w-5 text-green-600" />
                    <span className="text-gray-700">Unlimited RFP submissions</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <Check className="h-5 w-5 text-green-600" />
                    <span className="text-gray-700">Advanced vendor profile</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <Check className="h-5 w-5 text-green-600" />
                    <span className="text-gray-700">AI-powered RFP matching</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <Check className="h-5 w-5 text-green-600" />
                    <span className="text-gray-700">File attachments & portfolio</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <Check className="h-5 w-5 text-green-600" />
                    <span className="text-gray-700">Priority customer support</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <Check className="h-5 w-5 text-green-600" />
                    <span className="text-gray-700">Advanced analytics & insights</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <Check className="h-5 w-5 text-green-600" />
                    <span className="text-gray-700">Custom proposal templates</span>
                  </div>
                </div>
                <div className="pt-4">
                  <Link href="/register">
                    <Button className="w-full text-lg py-3 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700">
                      Start Pro Trial <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  </Link>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Feature Comparison */}
      <section className="py-20 px-4 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Compare Features
            </h2>
            <p className="text-xl text-gray-600">
              See exactly what's included in each plan
            </p>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full bg-white rounded-lg shadow-lg">
              <thead>
                <tr className="border-b">
                  <th className="text-left p-6 font-semibold text-gray-900">Features</th>
                  <th className="text-center p-6 font-semibold text-gray-900">Free</th>
                  <th className="text-center p-6 font-semibold text-gray-900 bg-blue-50">Pro</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b">
                  <td className="p-6 text-gray-700">Browse RFPs</td>
                  <td className="p-6 text-center"><Check className="h-5 w-5 text-green-600 mx-auto" /></td>
                  <td className="p-6 text-center bg-blue-50"><Check className="h-5 w-5 text-green-600 mx-auto" /></td>
                </tr>
                <tr className="border-b">
                  <td className="p-6 text-gray-700">Monthly Proposals</td>
                  <td className="p-6 text-center text-gray-600">3</td>
                  <td className="p-6 text-center bg-blue-50 text-blue-600 font-semibold">Unlimited</td>
                </tr>
                <tr className="border-b">
                  <td className="p-6 text-gray-700">AI-Powered Matching</td>
                  <td className="p-6 text-center text-gray-400">×</td>
                  <td className="p-6 text-center bg-blue-50"><Check className="h-5 w-5 text-green-600 mx-auto" /></td>
                </tr>
                <tr className="border-b">
                  <td className="p-6 text-gray-700">File Attachments</td>
                  <td className="p-6 text-center text-gray-400">×</td>
                  <td className="p-6 text-center bg-blue-50"><Check className="h-5 w-5 text-green-600 mx-auto" /></td>
                </tr>
                <tr className="border-b">
                  <td className="p-6 text-gray-700">Advanced Analytics</td>
                  <td className="p-6 text-center text-gray-400">×</td>
                  <td className="p-6 text-center bg-blue-50"><Check className="h-5 w-5 text-green-600 mx-auto" /></td>
                </tr>
                <tr className="border-b">
                  <td className="p-6 text-gray-700">Priority Support</td>
                  <td className="p-6 text-center text-gray-400">×</td>
                  <td className="p-6 text-center bg-blue-50"><Check className="h-5 w-5 text-green-600 mx-auto" /></td>
                </tr>
                <tr>
                  <td className="p-6 text-gray-700">Custom Templates</td>
                  <td className="p-6 text-center text-gray-400">×</td>
                  <td className="p-6 text-center bg-blue-50"><Check className="h-5 w-5 text-green-600 mx-auto" /></td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 px-4">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Frequently Asked Questions
            </h2>
            <p className="text-xl text-gray-600">
              Everything you need to know about our pricing
            </p>
          </div>

          <div className="space-y-6">
            <Card className="border-0 shadow-md">
              <CardHeader>
                <CardTitle className="flex items-center text-xl">
                  <HelpCircle className="h-5 w-5 mr-3 text-blue-600" />
                  What's included in the Free plan?
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600">
                  The Free plan includes access to browse unlimited RFPs, create a basic vendor profile, 
                  submit up to 3 proposals per month, basic messaging capabilities, and email support.
                </p>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-md">
              <CardHeader>
                <CardTitle className="flex items-center text-xl">
                  <HelpCircle className="h-5 w-5 mr-3 text-blue-600" />
                  Can I upgrade or downgrade my plan anytime?
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600">
                  Yes, you can upgrade or downgrade your plan at any time. Changes take effect immediately 
                  and billing is prorated. There are no long-term contracts or cancellation fees.
                </p>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-md">
              <CardHeader>
                <CardTitle className="flex items-center text-xl">
                  <HelpCircle className="h-5 w-5 mr-3 text-blue-600" />
                  Do you offer refunds?
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600">
                  We offer a 30-day money-back guarantee for all Pro plan subscriptions. 
                  If you're not satisfied, contact our support team for a full refund.
                </p>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-md">
              <CardHeader>
                <CardTitle className="flex items-center text-xl">
                  <HelpCircle className="h-5 w-5 mr-3 text-blue-600" />
                  Is there a setup fee or hidden costs?
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600">
                  No setup fees, no hidden costs. The price you see is exactly what you pay. 
                  We believe in transparent, straightforward pricing.
                </p>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-md">
              <CardHeader>
                <CardTitle className="flex items-center text-xl">
                  <HelpCircle className="h-5 w-5 mr-3 text-blue-600" />
                  What payment methods do you accept?
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600">
                  We accept all major credit cards (Visa, MasterCard, American Express) and PayPal. 
                  All payments are processed securely through Stripe.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 bg-gradient-to-r from-blue-600 to-purple-600 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Ready to Get Started?
          </h2>
          <p className="text-xl mb-8 opacity-90">
            Join thousands of businesses growing faster with SourcedIn's RFP marketplace.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link href="/register">
              <Button size="lg" variant="secondary" className="text-lg px-8 py-4">
                Start Free Trial <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
            <Link href="/request-for-proposal-marketplace">
              <Button size="lg" variant="outline" className="text-lg px-8 py-4 border-white text-white hover:bg-white hover:text-blue-600">
                Browse RFPs
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  )
}
